import { useState, useCallback, useRef, useEffect } from 'react';
// eslint-disable-next-line no-unused-vars
import { motion, AnimatePresence } from 'framer-motion';
import { Delete, Loader2 } from 'lucide-react';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const KEYS = [
  '1', '2', '3',
  '4', '5', '6',
  '7', '8', '9',
  'CLR', '0', 'DEL',
];

const SHAKE_TRANSITION = {
  x: {
    duration: 0.4,
    ease: 'easeInOut',
  },
};

const SHAKE_ANIMATION = {
  x: [0, -12, 12, -10, 10, -6, 6, 0],
};

// ---------------------------------------------------------------------------
// KioskPinPad
// ---------------------------------------------------------------------------
// Large touch-friendly PIN entry numpad.
//
// Props:
//   onVerifyPin  (function) - async, takes PIN string, returns { success, user, clockedIn, error }
//   onSuccess    (function) - called with (user, clockedIn, 'pin') on success
// ---------------------------------------------------------------------------

const PIN_LENGTH = 4;

export default function KioskPinPad({
  onVerifyPin,
  onSuccess,
}) {
  const [pin, setPin] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState(null);
  const [shouldShake, setShouldShake] = useState(false);

  // Ref to avoid stale closure in the auto-submit path.
  const isVerifyingRef = useRef(false);

  // -------------------------------------------------------------------------
  // Verify the entered PIN
  // -------------------------------------------------------------------------
  const verify = useCallback(
    async (currentPin) => {
      if (isVerifyingRef.current) return;

      isVerifyingRef.current = true;
      setIsVerifying(true);
      setError(null);

      try {
        const result = await onVerifyPin(currentPin);

        if (result?.success) {
          onSuccess?.(result.user, result.clockedIn, 'pin', result.onBreak, result.activeBreak);
        } else {
          // Shake, show error, clear PIN
          setShouldShake(true);
          setError(result?.error || 'Invalid PIN. Please try again.');
          setPin('');

          // Clear shake state after animation
          setTimeout(() => setShouldShake(false), 500);

          // Clear error after a few seconds
          setTimeout(() => setError(null), 3000);
        }
      } catch {
        setShouldShake(true);
        setError('Something went wrong. Please try again.');
        setPin('');
        setTimeout(() => setShouldShake(false), 500);
        setTimeout(() => setError(null), 3000);
      } finally {
        isVerifyingRef.current = false;
        setIsVerifying(false);
      }
    },
    [onVerifyPin, onSuccess],
  );

  // -------------------------------------------------------------------------
  // Handle key press
  // -------------------------------------------------------------------------
  const handleKey = useCallback(
    (key) => {
      if (isVerifyingRef.current) return;

      if (key === 'CLR') {
        setPin('');
        setError(null);
        return;
      }

      if (key === 'DEL') {
        setPin((prev) => prev.slice(0, -1));
        setError(null);
        return;
      }

      // Digit
      setPin((prev) => {
        if (prev.length >= PIN_LENGTH) return prev;

        const next = prev + key;

        // Auto-submit when all digits are entered
        if (next.length === PIN_LENGTH) {
          // Use setTimeout to let React flush the state update first so
          // the dot indicators show the final filled dot before verifying.
          setTimeout(() => verify(next), 80);
        }

        return next;
      });

      setError(null);
    },
    [PIN_LENGTH, verify],
  );

  // -------------------------------------------------------------------------
  // Physical keyboard support
  // -------------------------------------------------------------------------
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key >= '0' && e.key <= '9') {
        handleKey(e.key);
      } else if (e.key === 'Backspace') {
        handleKey('DEL');
      } else if (e.key === 'Escape') {
        handleKey('CLR');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKey]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <div className="flex flex-col items-center gap-8">
      {/* Title */}
      <h2 className="text-2xl font-semibold text-slate-800">
        Enter your PIN
      </h2>

      {/* Dot indicators */}
      <motion.div
        className="flex items-center gap-3"
        animate={shouldShake ? SHAKE_ANIMATION : { x: 0 }}
        transition={SHAKE_TRANSITION}
      >
        {Array.from({ length: PIN_LENGTH }).map((_, i) => {
          const filled = i < pin.length;
          return (
            <motion.div
              key={i}
              className={
                'w-5 h-5 rounded-full border-2 transition-colors ' +
                (filled
                  ? 'bg-slate-800 border-slate-800'
                  : 'bg-transparent border-slate-300')
              }
              animate={filled ? { scale: [1, 1.3, 1] } : { scale: 1 }}
              transition={{ duration: 0.2 }}
            />
          );
        })}
      </motion.div>

      {/* Error text */}
      <AnimatePresence>
        {error && (
          <motion.p
            className="text-lg font-medium text-red-500 text-center"
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
          >
            {error}
          </motion.p>
        )}
      </AnimatePresence>

      {/* Loading overlay */}
      {isVerifying && (
        <motion.div
          className="flex items-center gap-3 text-xl text-slate-500"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <Loader2 className="w-6 h-6 animate-spin" />
          <span>Verifying...</span>
        </motion.div>
      )}

      {/* Keypad grid */}
      {!isVerifying && (
        <div className="grid grid-cols-3 gap-4">
          {KEYS.map((key) => {
            const isAction = key === 'CLR' || key === 'DEL';

            return (
              <motion.button
                key={key}
                type="button"
                className={
                  'w-20 h-20 rounded-2xl font-semibold flex items-center justify-center transition-colors select-none ' +
                  (isAction
                    ? 'bg-slate-200 text-slate-600 text-lg hover:bg-slate-300 active:bg-slate-300'
                    : 'bg-white text-slate-800 text-2xl border border-slate-200 shadow-sm hover:bg-slate-50 active:bg-slate-100')
                }
                whileTap={{ scale: 0.92 }}
                onClick={() => handleKey(key)}
                aria-label={
                  key === 'DEL'
                    ? 'Delete last digit'
                    : key === 'CLR'
                      ? 'Clear all digits'
                      : `Digit ${key}`
                }
              >
                {key === 'DEL' ? (
                  <Delete className="w-6 h-6" />
                ) : (
                  key
                )}
              </motion.button>
            );
          })}
        </div>
      )}
    </div>
  );
}
